import { forwardRef, useState, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { ArrowDown, ArrowUp, CalendarRange, Check, ListFilter, X } from "lucide-react";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import UserAvatar from "@/components/UserAvatar";
import CheckAllBar from "@/components/shared/CheckAllBar";
import { cn } from "@/lib/utils";
import type { DateRangeFilter, SortDirection } from "@/types/backlog";

export interface HeaderFilterOption {
  id: string;
  label: string;
  /** Colour dot marker (statuses, priorities). */
  color?: string;
  /** Avatar marker (people). */
  profile?: {
    avatar_url: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string;
  };
}

/**
 * The sort half of a column header's dropdown.
 *
 * Sorting is stacked, not exclusive — several columns can be sorting at once
 * and the order they were added in is what the table obeys — so a header has
 * to show two things a single-sort arrow never did: THAT it is sorting, and
 * WHERE in the stack it sits. `index` is that position, 1-based, and null when
 * this column is not in the stack at all.
 */
export interface HeaderSortControl {
  index: number | null;
  direction: SortDirection | null;
  /** Choosing the direction already applied removes the sort (see useBacklogFilters). */
  onToggle: (direction: SortDirection) => void;
  onRemove: () => void;
  /** How the two directions read for this column — dates say "Newest first". */
  ascLabel: string;
  descLabel: string;
}

/**
 * The badge on a sorting header: direction arrow plus the sort's position.
 *
 * Deliberately not the same shape as the filter's count chip. Both can sit on
 * one header, and "2nd in the sort" must not be mistaken for "2 values
 * selected" — which is exactly what two identical pills would say.
 */
const SortBadge = ({ index, direction }: { index: number; direction: SortDirection | null }) => (
  <span className="th-sort-badge">
    {direction === "desc" ? <ArrowDown className="h-2.5 w-2.5" /> : <ArrowUp className="h-2.5 w-2.5" />}
    {index}
  </span>
);

/**
 * The header button itself, shared by every column control below so that a
 * filterable header, a sortable-only header and a date header are one object
 * with different contents rather than three that drifted apart.
 */
interface HeaderTriggerProps extends ComponentPropsWithoutRef<"button"> {
  label: string;
  filterCount?: number;
  sortIndex?: number | null;
  sortDirection?: SortDirection | null;
  restIcon?: ReactNode;
}

export const HeaderTrigger = forwardRef<HTMLButtonElement, HeaderTriggerProps>(
  ({ label, filterCount = 0, sortIndex = null, sortDirection = null, restIcon, className, ...rest }, ref) => {
    const sorted = sortIndex !== null;
    const filtering = filterCount > 0;
    return (
      <button
        ref={ref}
        type="button"
        {...rest}
        className={cn(
          "th-filter press-pop",
          sorted && "th-sorted",
          // `.th-active`, not `text-primary`: --primary is a navy that all but
          // vanishes on the dark card, so a sorted or filtering header was the
          // one header in the row you could not read in dark mode. The class
          // resolves to the brand navy in light and the ring cyan in dark,
          // exactly as the hover state already did.
          filtering || sorted ? "th-active" : "text-foreground",
          className,
        )}
      >
        <span className="th-filter-label">{label}</span>
        {/* `shrink-0` on both adornments: the trigger is now the width of its
            column (see `.th-filter`), and when that is tight the label is what
            gives — never the count or the sort position, which are the two
            things a squeezed header still has to be able to say. */}
        {filtering && (
          <span className="shrink-0 rounded-full bg-primary px-1.5 text-[10px] font-bold leading-4 text-primary-foreground">
            {filterCount}
          </span>
        )}
        {sorted && <SortBadge index={sortIndex} direction={sortDirection} />}
        {/* The standing affordance, shown only while the header has nothing of
            its own to say — a badge and an icon competing for the same 12px
            read as clutter on a column header this size. */}
        {!filtering && !sorted && restIcon}
      </button>
    );
  },
);
HeaderTrigger.displayName = "HeaderTrigger";

/**
 * The sort block at the top of every sortable header's dropdown.
 *
 * Removal is spelled twice on purpose. Clicking the active direction again
 * takes the column out of the stack (the click that turned it on turns it
 * off), and the explicit row below says so for anyone who would never guess
 * that a highlighted option is also a toggle.
 */
export const SortSection = ({ sort }: { sort: HeaderSortControl }) => {
  const row = "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs transition-colors";
  return (
    <div className="border-b border-border p-1">
      <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Sort</p>
      {(["asc", "desc"] as const).map((direction) => {
        const active = sort.direction === direction;
        return (
          <button
            key={direction}
            type="button"
            onClick={() => sort.onToggle(direction)}
            title={active ? "Click again to remove this sort" : undefined}
            className={cn(
              row,
              active
                ? "bg-[hsl(var(--ring)/0.15)] font-semibold text-brand"
                : "text-foreground hover:bg-[hsl(var(--ring)/0.08)]",
            )}
          >
            {direction === "asc" ? (
              <ArrowUp className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <ArrowDown className="h-3.5 w-3.5 shrink-0" />
            )}
            <span className="flex-1 text-left">{direction === "asc" ? sort.ascLabel : sort.descLabel}</span>
            {active && <Check className="h-3.5 w-3.5 shrink-0" />}
          </button>
        );
      })}
      {sort.index != null && (
        <button
          type="button"
          onClick={sort.onRemove}
          className={cn(row, "text-muted-foreground hover:bg-[hsl(var(--ring)/0.08)] hover:text-foreground")}
        >
          <X className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1 text-left">Remove from sort</span>
          <SortBadge index={sort.index} direction={sort.direction} />
        </button>
      )}
    </div>
  );
};

interface HeaderFilterProps {
  label: string;
  options: HeaderFilterOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  /** Omit to drop the search input (short lists like priorities). */
  searchPlaceholder?: string;
  align?: "start" | "center" | "end";
  /** Omit for a column that filters but cannot be sorted (Added By). */
  sort?: HeaderSortControl;
  /**
   * Extra classes for the trigger. The trigger fills its cell, so a column
   * whose data is centred (Priority's flag) has to say so — `text-center` on
   * the `th` no longer reaches a flex child that spans the whole cell.
   */
  triggerClassName?: string;
}

/**
 * The option half of a filtering header's dropdown: ticks with a search box,
 * avatars or colour dots, and Check all / Uncheck all (#9764). Its
 * own piece so a header that composes its dropdown differently (the custom
 * Fields column, #9399) ticks options exactly the way every other header does.
 */
export const HeaderOptionList = ({
  options,
  selected,
  onChange,
  searchPlaceholder,
}: Pick<HeaderFilterProps, "options" | "selected" | "onChange" | "searchPlaceholder">) => {
  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  };
  return (
    <>
      <Command className="bg-card">
        {searchPlaceholder && <CommandInput placeholder={searchPlaceholder} className="text-foreground" />}
        {/* Check all / Uncheck all (#9764). Uncheck all is also how the
            filter is cleared. */}
        <CheckAllBar
          checked={options.filter((o) => selected.includes(o.id)).length}
          total={options.length}
          onChange={(on) => onChange(on ? options.map((o) => o.id) : [])}
          className="border-b border-border px-2 py-1"
        />
        <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">No match.</CommandEmpty>
        <CommandGroup className="max-h-64 overflow-y-auto bg-card">
          {options.map((option) => (
            <CommandItem
              key={option.id}
              value={option.label}
              onSelect={() => toggle(option.id)}
              className="text-foreground"
            >
              <Check
                className={cn(
                  "mr-2 h-4 w-4 shrink-0 text-brand",
                  selected.includes(option.id) ? "opacity-100" : "opacity-0",
                )}
              />
              {option.profile ? (
                <UserAvatar
                  avatarUrl={option.profile.avatar_url}
                  firstName={option.profile.first_name}
                  lastName={option.profile.last_name}
                  email={option.profile.email}
                  className="mr-2 h-5 w-5"
                  fallbackClassName="text-[9px]"
                />
              ) : option.color ? (
                <span className="mr-2 h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: option.color }} />
              ) : null}
              <span className="truncate">{option.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </Command>
    </>
  );
};

/**
 * A column header that is its own filter and its own sort: click the label,
 * pick an order or narrow to some values, and the column obeys. Replaces the
 * separate "Add Filter" row and the toolbar's Sort button — both controls live
 * where the data they act on is, and the header itself shows when either is
 * active (brand colour + count chip, blue tint + numbered badge).
 */
export const HeaderFilter = ({
  label,
  options,
  selected,
  onChange,
  searchPlaceholder,
  align,
  sort,
  triggerClassName,
}: HeaderFilterProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <HeaderTrigger
          label={label}
          title={sort ? `Sort or filter by ${label.toLowerCase()}` : `Filter by ${label.toLowerCase()}`}
          filterCount={selected.length}
          sortIndex={sort?.index ?? null}
          sortDirection={sort?.direction ?? null}
          restIcon={<ListFilter className="th-filter-icon h-3 w-3 shrink-0" />}
          className={triggerClassName}
        />
      </PopoverTrigger>
      <PopoverContent align={align ?? "start"} className="w-56 border-border bg-card p-0">
        {sort && <SortSection sort={sort} />}
        <HeaderOptionList
          options={options}
          selected={selected}
          onChange={onChange}
          searchPlaceholder={searchPlaceholder}
        />
      </PopoverContent>
    </Popover>
  );
};

/* There is no sort-only header. Task briefly had one — an A→Z on the ticket
   title — and it went with the alphabetical Assigned sort for the same reason:
   nobody looks through a backlog for the ticket whose name starts with "A".
   Sorting is Priority and the two dates. */

const toDate = (value: string | null) => (value ? new Date(`${value}T00:00:00`) : undefined);
const toValue = (date: Date | undefined) => (date ? format(date, "yyyy-MM-dd") : null);

/** "Jan 4 – Jan 9", or one open end of it. */
const rangeLabel = (range: DateRangeFilter) => {
  const from = toDate(range.from);
  const to = toDate(range.to);
  if (from && to) return `${format(from, "MMM d")} – ${format(to, "MMM d")}`;
  if (from) return `From ${format(from, "MMM d")}`;
  if (to) return `Until ${format(to, "MMM d")}`;
  return null;
};

interface HeaderDateFilterProps {
  label: string;
  sort: HeaderSortControl;
  range: DateRangeFilter;
  onRangeChange: (range: DateRangeFilter) => void;
  /** As on HeaderFilter: the trigger fills its cell, so a centred column says so here. */
  triggerClassName?: string;
}

/**
 * The Created / Last updated dropdown: order the column, or narrow it to a
 * span of days.
 *
 * A date column's values are a continuum, so the multi-select the other
 * headers use makes no sense here — the filter is a range, and the range is a
 * calendar. It stays folded until asked for, because the common use of this
 * dropdown is "newest first" and a calendar opening under every click would
 * bury the two options that answer it.
 */
export const HeaderDateFilter = ({ label, sort, range, onRangeChange, triggerClassName }: HeaderDateFilterProps) => {
  const [open, setOpen] = useState(false);
  const rangeActive = range.from !== null || range.to !== null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <HeaderTrigger
          label={label}
          title={`Sort or filter by ${label.toLowerCase()}`}
          filterCount={rangeActive ? 1 : 0}
          sortIndex={sort.index}
          sortDirection={sort.direction}
          restIcon={<ListFilter className="th-filter-icon h-3 w-3 shrink-0" />}
          className={triggerClassName}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="max-h-[75vh] w-auto overflow-y-auto border-border bg-card p-0">
        <SortSection sort={sort} />
        <DateRangeSection range={range} onRangeChange={onRangeChange} />
        {/* The "Active in the last N hours/days" control that used to hang off
            Created is gone. It asked a recency question against the activity
            log; a from/to range on Last updated answers the same question
            against the column it is written under, and keeping both would have
            been two controls for one idea in one dropdown. */}
      </PopoverContent>
    </Popover>
  );
};

/**
 * A dropdown's "Date range…" row and the calendar it unfolds, with its clear
 * button. Its own piece so the custom Fields header (#9764) filters a date
 * field exactly the way Created and Updated do. Mounted only while its
 * dropdown is open, so it always opens folded unless a range is set.
 */
export const DateRangeSection = ({ range, onRangeChange }: Pick<HeaderDateFilterProps, "range" | "onRangeChange">) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const rangeActive = range.from !== null || range.to !== null;
  const selectedRange: DateRange | undefined = rangeActive
    ? { from: toDate(range.from), to: toDate(range.to) }
    : undefined;
  return (
    <div className="p-1">
      <button
        type="button"
        onClick={() => setShowCalendar((v) => !v)}
        className={cn(
          "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs transition-colors",
          rangeActive ? "font-semibold text-brand" : "text-foreground",
        )}
      >
        <CalendarRange className="h-3.5 w-3.5 shrink-0" />
        <span className="flex-1 text-left">Date range…</span>
        {rangeActive && (
          <span className="rounded bg-primary px-1.5 text-[10px] font-bold leading-4 text-primary-foreground">
            {rangeLabel(range)}
          </span>
        )}
      </button>
      {(showCalendar || rangeActive) && (
        <>
          <Calendar
            mode="range"
            selected={selectedRange}
            onSelect={(next) => onRangeChange({ from: toValue(next?.from), to: toValue(next?.to) })}
            numberOfMonths={1}
          />
          {rangeActive && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-full justify-start gap-1.5 px-2 text-xs text-muted-foreground"
              onClick={() => onRangeChange({ from: null, to: null })}
            >
              <X className="h-3 w-3" />
              Clear date range
            </Button>
          )}
        </>
      )}
    </div>
  );
};
